import { buildInventoryWorkspacePath } from "@/components/operations/operations-shared";
import { Button } from "@/components/ui/button";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { GlobalSearchHit, SearchableEntityType } from "@/core";
import { useGlobalSearch } from "@/hooks";
import { Loader2, Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

/**
 * Where each record type opens. Kept on the client so the search function stays
 * ignorant of routing.
 *
 * Invoices and payments land on their module rather than the individual record:
 * neither has a deep-linkable detail view today, and pretending otherwise would
 * navigate somewhere blank.
 */
function buildHitPath(payload: { hit: GlobalSearchHit }): string {
  const { hit } = payload;

  switch (hit.type) {
    case "client":
      return `/clients?clientId=${hit.id}`;
    case "vendor":
      return `/vendors?vendorId=${hit.id}`;
    case "customer":
      return "/leads";
    case "inventory":
      return buildInventoryWorkspacePath({ inventoryId: hit.id });
    case "purchase-order":
      return `/operations/purchase-orders?poId=${hit.id}`;
    case "sales-order":
      return `/operations/sales-orders?soId=${hit.id}`;
    case "deal":
      return `/deals?dealId=${hit.id}`;
    case "order-candidate":
      return `/operations/order-suggestions?candidateId=${hit.id}`;
    case "invoice":
      return hit.routeContext?.inventoryId
        ? buildInventoryWorkspacePath({
            inventoryId: hit.routeContext.inventoryId,
            invoiceId: hit.id,
            focus: "purchase-financials",
          })
        : "/operations/finance";
    case "payment":
      return "/operations/payments";
    default:
      return "/dashboard";
  }
}

/**
 * Groups hits while preserving the server's ranking: groups appear in the order
 * their best hit ranked, so the most relevant record stays at the top of the list
 * instead of being pushed down by a fixed group order.
 */
function groupHits(payload: { hits: GlobalSearchHit[] }) {
  const groups = new Map<
    SearchableEntityType,
    { groupLabel: string; hits: GlobalSearchHit[] }
  >();

  for (const hit of payload.hits) {
    const existing = groups.get(hit.type);

    if (existing) {
      existing.hits.push(hit);
    } else {
      groups.set(hit.type, { groupLabel: hit.groupLabel, hits: [hit] });
    }
  }

  return [...groups.entries()].map(([type, group]) => ({ type, ...group }));
}

export function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const navigate = useNavigate();
  const { hits, isSearching, isQueryTooShort, isError, truncatedTypes } =
    useGlobalSearch({ query });

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "k" && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        setOpen((previous) => !previous);
      }
    };

    document.addEventListener("keydown", onKeyDown);

    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  useEffect(() => {
    if (!open) {
      setQuery("");
    }
  }, [open]);

  const groups = useMemo(() => groupHits({ hits }), [hits]);

  const handleSelect = (hit: GlobalSearchHit) => {
    setOpen(false);
    navigate(buildHitPath({ hit }));
  };

  return (
    <>
      <Button
        variant="outline"
        className="text-muted-foreground w-full max-w-xs justify-start font-normal"
        onClick={() => setOpen(true)}
      >
        <Search className="mr-2 h-4 w-4" />
        <span className="flex-1 text-left">Search everything...</span>
        <kbd className="bg-muted pointer-events-none hidden select-none rounded border px-1.5 font-mono text-[10px] sm:inline-block">
          ⌘K
        </kbd>
      </Button>

      <CommandDialog
        open={open}
        onOpenChange={setOpen}
        title="Search"
        description="Find clients, vendors, inventory, orders, customers and deals"
        // Ranking comes from the server, which can see the whole match; cmdk's
        // fuzzy scoring would re-order results using only the visible label.
        shouldFilter={false}
      >
        <CommandInput
          placeholder="Search clients, vendors, inventory, orders, deals..."
          value={query}
          onValueChange={setQuery}
        />
        <CommandList>
          {isSearching ? (
            <div className="text-muted-foreground flex items-center justify-center gap-2 py-6 text-sm">
              <Loader2 className="h-4 w-4 animate-spin" />
              Searching...
            </div>
          ) : null}

          {!isSearching && isError ? (
            <div className="text-destructive py-6 text-center text-sm">
              Search is unavailable right now. Please try again.
            </div>
          ) : null}

          {!isSearching && !isError && isQueryTooShort ? (
            <div className="text-muted-foreground py-6 text-center text-sm">
              Keep typing — at least 2 characters.
            </div>
          ) : null}

          {!isSearching && !isError && !isQueryTooShort && query.trim() ? (
            <CommandEmpty>No records found.</CommandEmpty>
          ) : null}

          {groups.map((group) => (
            <CommandGroup key={group.type} heading={group.groupLabel}>
              {group.hits.map((hit) => (
                <CommandItem
                  key={`${hit.type}:${hit.id}`}
                  value={`${hit.type}:${hit.id}`}
                  onSelect={() => handleSelect(hit)}
                  className="flex flex-col items-start gap-0.5"
                >
                  <span className="truncate font-medium">{hit.label}</span>
                  {hit.sublabel ? (
                    <span className="text-muted-foreground truncate text-xs">
                      {hit.sublabel}
                    </span>
                  ) : null}
                </CommandItem>
              ))}
            </CommandGroup>
          ))}

          {truncatedTypes.length > 0 ? (
            <div className="text-muted-foreground border-t px-3 py-2 text-xs">
              Showing the closest matches only — narrow the search to see more.
            </div>
          ) : null}
        </CommandList>
      </CommandDialog>
    </>
  );
}
